/**
 * STT Service - Whisper WebGPU
 * Live speech-to-text transcription using Whisper in the browser
 */
import { pipeline } from '@huggingface/transformers';

class STTService {
    constructor() {
        this.transcriber = null;
        this.isLoading = false;
        this.isReady = false;
        this.isRecording = false;
        this.stream = null;
        this.audioContext = null;
        this.sourceNode = null;
        this.processorNode = null;
        this.audioChunks = [];
    }

    /**
     * Initialize Whisper transcriber
     */
    async init(onProgress = () => { }) {
        if (this.isLoading) return false;
        if (this.isReady) return true;

        this.isLoading = true;

        try {
            onProgress({ text: 'Chargement Whisper...', progress: 10 });

            // Use whisper-tiny for fast loading, WebGPU accelerated
            this.transcriber = await pipeline(
                'automatic-speech-recognition',
                'onnx-community/whisper-tiny',
                {
                    dtype: 'fp32',
                    device: 'webgpu',
                }
            );

            this.isReady = true;
            this.isLoading = false;
            onProgress({ text: 'Whisper prêt !', progress: 100 });
            return true;
        } catch (error) {
            console.error('WebGPU STT failed, trying WASM:', error);

            try {
                onProgress({ text: 'Fallback WASM...', progress: 50 });

                this.transcriber = await pipeline(
                    'automatic-speech-recognition',
                    'onnx-community/whisper-tiny',
                    {
                        dtype: 'q8',
                        device: 'wasm',
                    }
                );

                this.isReady = true;
                this.isLoading = false;
                onProgress({ text: 'Whisper prêt (WASM)', progress: 100 });
                return true;
            } catch (fallbackError) {
                this.isLoading = false;
                throw fallbackError;
            }
        }
    }

    /**
     * Start recording from microphone using Web Audio API
     */
    async startRecording() {
        if (this.isRecording) return;

        try {
            this.stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    sampleRate: 16000,
                    channelCount: 1,
                    echoCancellation: true,
                    noiseSuppression: true,
                }
            });

            // Create audio context at 16kHz for Whisper
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
                sampleRate: 16000
            });

            this.sourceNode = this.audioContext.createMediaStreamSource(this.stream);

            // Use ScriptProcessorNode to capture raw PCM data
            // Buffer size of 4096 samples
            this.processorNode = this.audioContext.createScriptProcessor(4096, 1, 1);
            this.audioChunks = [];

            this.processorNode.onaudioprocess = (e) => {
                if (this.isRecording) {
                    const inputData = e.inputBuffer.getChannelData(0);
                    // Clone the data because it gets reused
                    this.audioChunks.push(new Float32Array(inputData));
                }
            };

            this.sourceNode.connect(this.processorNode);
            this.processorNode.connect(this.audioContext.destination);

            this.isRecording = true;
            console.log('Recording started (Web Audio API)');
        } catch (error) {
            console.error('Failed to start recording:', error);
            throw error;
        }
    }

    /**
     * Stop recording and transcribe
     * @param {string} language - Language code (e.g., 'fr', 'en', 'es')
     */
    async stopRecording(language = null) {
        if (!this.isRecording) {
            return null;
        }

        this.isRecording = false;

        // Disconnect nodes
        if (this.processorNode) {
            this.processorNode.disconnect();
        }
        if (this.sourceNode) {
            this.sourceNode.disconnect();
        }

        // Stop all tracks
        if (this.stream) {
            this.stream.getTracks().forEach(track => track.stop());
        }

        // Close audio context
        if (this.audioContext && this.audioContext.state !== 'closed') {
            await this.audioContext.close();
        }

        // Concatenate all audio chunks into one Float32Array
        const totalLength = this.audioChunks.reduce((sum, chunk) => sum + chunk.length, 0);
        const audioData = new Float32Array(totalLength);
        let offset = 0;
        for (const chunk of this.audioChunks) {
            audioData.set(chunk, offset);
            offset += chunk.length;
        }

        console.log(`Captured ${totalLength} samples (${(totalLength / 16000).toFixed(1)}s)`);

        // Transcribe if we have audio data
        if (audioData.length > 1600 && this.transcriber) { // At least 0.1s
            try {
                // Map language codes to Whisper language names
                const languageMap = {
                    'fr': 'french',
                    'en': 'english',
                    'es': 'spanish',
                    'de': 'german',
                    'it': 'italian',
                    'pt': 'portuguese',
                    'ja': 'japanese',
                    'zh': 'chinese',
                };

                const whisperLang = language ? languageMap[language] || language : null;
                console.log(`Transcribing audio... (language: ${whisperLang || 'auto'})`);

                const options = { task: 'transcribe' };
                if (whisperLang) {
                    options.language = whisperLang;
                }

                const result = await this.transcriber(audioData, options);
                console.log('Transcription:', result);
                return result.text || '';
            } catch (error) {
                console.error('Transcription error:', error);
                throw error;
            }
        }

        return '';
    }

    /**
     * Cancel recording without transcribing
     */
    cancelRecording() {
        this.isRecording = false;

        if (this.processorNode) {
            this.processorNode.disconnect();
        }
        if (this.sourceNode) {
            this.sourceNode.disconnect();
        }
        if (this.stream) {
            this.stream.getTracks().forEach(track => track.stop());
        }
        if (this.audioContext && this.audioContext.state !== 'closed') {
            this.audioContext.close();
        }

        this.audioChunks = [];
    }

    getStatus() {
        return {
            isLoading: this.isLoading,
            isReady: this.isReady,
            isRecording: this.isRecording
        };
    }
}

export const sttService = new STTService();
export default sttService;
